import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { ComponentType, useCallback, useContext, useMemo } from 'react';
import { AppContext } from '../../../context/AppContext';
import { ContextDataItem } from '../../../types/widget';
import { TOOLTIP_Z_INDEX } from '../../Recorder/styles/recorderStyles';
import { contextPanelStyles } from '../styles/ContextPanelStyles';
import { selectContextItemStyles as styles } from '../styles/SelectContextItemStyles';

interface SelectContextItemProps {
  item: ContextDataItem;
}

/**
 * Generic single-select dropdown component for context items
 * Renders a checkbox that controls visibility of a single-select dropdown
 * Uses children for options - each child has key, label, value, and selected
 */
export const SelectContextItem: ComponentType<SelectContextItemProps> = ({
  item,
}) => {
  const { sessionModel, updateSessionModel } = useContext(AppContext);

  const itemKey = item.key;

  const contextDataItem = useMemo(() => {
    return sessionModel.contextData.find((i) => i.key === itemKey);
  }, [sessionModel.contextData, itemKey]);

  const children = useMemo(
    () => contextDataItem?.children || [],
    [contextDataItem?.children]
  );

  const hasNullValue = item.value === null;

  const isChecked = hasNullValue ? false : (contextDataItem?.selected ?? false);

  const selectedKey = useMemo(() => {
    const selected = children.find((child) => child.selected);
    // Default to first child if none selected
    return selected?.key || (children.length > 0 ? children[0].key : '');
  }, [children]);

  const handleCheckboxChange = useCallback(() => {
    if (hasNullValue) return;
    const newChecked = !isChecked;

    updateSessionModel((prev) => {
      return {
        ...prev,
        contextData: prev.contextData.map((dataItem) => {
          if (dataItem.key !== itemKey) return dataItem;

          const updatedChildren = dataItem.children?.map((child, index) => ({
            ...child,
            // When enabling, select first child by default if none selected
            selected: newChecked
              ? selectedKey === child.key || (selectedKey === '' && index === 0)
              : false,
          }));

          const selectedValue = newChecked
            ? updatedChildren?.find((c) => c.selected)?.value || ''
            : '';

          return {
            ...dataItem,
            selected: newChecked,
            children: updatedChildren,
            value: selectedValue,
          };
        }),
      };
    });
  }, [isChecked, selectedKey, itemKey, updateSessionModel, hasNullValue]);

  const handleDropdownChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newSelectedKeyString = event.target.value;
      const newSelectedKey = children.find(
        (child) => String(child.key) === newSelectedKeyString
      )?.key;

      if (newSelectedKey === undefined) return;

      updateSessionModel((prev) => {
        return {
          ...prev,
          contextData: prev.contextData.map((dataItem) => {
            if (dataItem.key !== itemKey) return dataItem;

            const updatedChildren = dataItem.children?.map((child) => ({
              ...child,
              selected: child.key === newSelectedKey,
            }));

            const selectedValue =
              updatedChildren?.find((c) => c.selected)?.value || '';

            return {
              ...dataItem,
              selected: true, // Parent stays selected when making dropdown changes
              children: updatedChildren,
              value: selectedValue,
            };
          }),
        };
      });
    },
    [itemKey, updateSessionModel, children]
  );

  const renderValue = useCallback(
    (selected: string) => {
      if (!selected) {
        return <Typography variant='placeholder'>Select an item</Typography>;
      }

      const child = children.find((c) => String(c.key) === selected);
      if (child) {
        return (
          <Typography variant='body2' sx={styles.selectedValueText}>
            {child.label}
          </Typography>
        );
      }

      return <Typography variant='placeholder'>Select an item</Typography>;
    },
    [children]
  );

  if (children.length === 0 && !hasNullValue) {
    return null;
  }

  if (children.length === 0) {
    return (
      <Box sx={styles.container}>
        <Tooltip
          title='No data available'
          arrow
          placement='left'
          slotProps={{
            popper: {
              sx: { zIndex: TOOLTIP_Z_INDEX },
            },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={false}
                disabled
                size='medium'
                sx={contextPanelStyles.checkbox}
              />
            }
            label={
              <Typography sx={contextPanelStyles.contextItemTextDisabled}>
                {item.label}
              </Typography>
            }
            sx={contextPanelStyles.contextItemLabel}
          />
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={handleCheckboxChange}
            disabled={hasNullValue}
            size='medium'
            sx={contextPanelStyles.checkbox}
          />
        }
        label={
          <Typography
            sx={
              hasNullValue
                ? contextPanelStyles.contextItemTextDisabled
                : contextPanelStyles.contextItemText
            }
          >
            {item.label}
          </Typography>
        }
        sx={contextPanelStyles.contextItemLabel}
      />

      {isChecked && (
        <Box sx={styles.dropdownContainer}>
          <FormControl sx={styles.formControl}>
            <Select
              value={String(selectedKey)}
              onChange={handleDropdownChange}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={renderValue}
              sx={styles.select}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                disableScrollLock: true,
                PaperProps: {
                  sx: styles.menuPaper,
                },
                sx: styles.menu,
              }}
            >
              {children.map((child) => (
                <MenuItem
                  key={child.key}
                  value={String(child.key)}
                  sx={styles.menuItem}
                >
                  <Typography variant='body2' sx={styles.selectedValueText}>
                    {child.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};
